import { useEffect, useMemo, useState } from "react";
import { Clock, HardHat, Zap, Timer, Users, Info } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LabelList,
} from "recharts";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatCard from "@/components/StatCard";
import { listClientes } from "@/lib/api";
import { fmtBRL, fmtInt, fmtPct } from "@/lib/format";

const PLAN_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#ec4899", "#0ea5e9", "#f97316",
];

// Horas de trabalho úteis por técnico por mês (base p/ recomendação)
const HORAS_TECNICO_MES = 160;

const monthLabelPT = (yyyyMm) => {
  const [y, m] = yyyyMm.split("-");
  const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  return `${meses[parseInt(m, 10) - 1] || m}/${y}`;
};

export default function ProdutividadeCard({ plans = [] }) {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthFilter, setMonthFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setClientes(await listClientes());
      } catch (err) {
        console.error("Erro ao carregar clientes para produtividade:", err);
        toast.error("Erro ao carregar clientes para produtividade");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const availableMonths = useMemo(() => {
    const set = new Set();
    for (const c of clientes) if (c.created_at) set.add(c.created_at.slice(0, 7));
    return [...set].sort().reverse();
  }, [clientes]);

  const clientesFiltrados = useMemo(() => {
    if (!monthFilter) return clientes;
    return clientes.filter((c) => (c.created_at || "").startsWith(monthFilter));
  }, [clientes, monthFilter]);

  const rows = useMemo(() => {
    const counter = new Map();
    for (const c of clientesFiltrados) {
      const name = c.plano_escolhido || "—";
      counter.set(name, (counter.get(name) || 0) + 1);
    }
    return plans.map((p) => {
      const vendas = counter.get(p.name) || 0;
      const horasPorInstalacao = Number(p.custo_hora_homem_qty || 0);
      const custoHoraUnit = Number(p.custo_hora_homem_unit || 0);
      const custoMOInstalacao = horasPorInstalacao * custoHoraUnit;
      const price = Number(p.price || 0);
      // custo total (soma das partes)
      const custoTotal =
        Number(p.custo_drop || 0) +
        Number(p.custo_conector || 0) +
        Number(p.custo_mega || 0) +
        Number(p.custo_equipamento || 0) +
        Number(p.custo_imposto || 0) +
        custoMOInstalacao;
      const pctMOnoCusto = custoTotal > 0 ? (custoMOInstalacao / custoTotal) * 100 : 0;
      const horasTotais = horasPorInstalacao * vendas;
      const custoMOTotal = custoMOInstalacao * vendas;
      return {
        plano: p.name,
        horasPorInstalacao,
        custoHoraUnit,
        custoMOInstalacao,
        pctMOnoCusto,
        vendas,
        horasTotais,
        custoMOTotal,
        price,
        eficiencia: price > 0 ? (custoMOInstalacao / price) * 100 : 0,
      };
    });
  }, [plans, clientesFiltrados]);

  const totais = useMemo(() => {
    const horasTotais = rows.reduce((s, r) => s + r.horasTotais, 0);
    const custoMOTotal = rows.reduce((s, r) => s + r.custoMOTotal, 0);
    const vendas = rows.reduce((s, r) => s + r.vendas, 0);
    return { horasTotais, custoMOTotal, vendas };
  }, [rows]);

  const maisPesado = useMemo(
    () => [...rows].filter((r) => r.horasPorInstalacao > 0).sort((a, b) => b.horasPorInstalacao - a.horasPorInstalacao)[0],
    [rows]
  );
  const maisLeve = useMemo(
    () => [...rows].filter((r) => r.horasPorInstalacao > 0).sort((a, b) => a.horasPorInstalacao - b.horasPorInstalacao)[0],
    [rows]
  );

  const chartData = useMemo(
    () => rows.filter((r) => r.horasTotais > 0).map((r) => ({
      plano: r.plano, horas: Number(r.horasTotais.toFixed(2)), vendas: r.vendas,
      horasUnit: r.horasPorInstalacao,
    })),
    [rows]
  );

  // recomendação de técnicos: ceil(horas totais / 160h por mês)
  const tecnicosRecomendados = totais.horasTotais > 0
    ? Math.ceil(totais.horasTotais / HORAS_TECNICO_MES)
    : 0;

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="produtividade-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <HardHat className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Produtividade & alocação</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline">Cruzando MO × vendas</Badge>
          <Select value={monthFilter || "all"} onValueChange={(v) => setMonthFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]" data-testid="filter-produtividade-month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os meses</SelectItem>
              {availableMonths.map((m) => (
                <SelectItem key={m} value={m}>{monthLabelPT(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-8">Carregando dados de produtividade…</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <StatCard testId="prod-kpi-horas" label="Horas de instalação"
              value={`${totais.horasTotais.toFixed(1)}h`}
              hint={`${fmtInt(totais.vendas)} venda(s) no período`}
              icon={Clock} tone="warning" />
            <StatCard testId="prod-kpi-custo-mo" label="Custo total de MO"
              value={fmtBRL(totais.custoMOTotal)}
              hint="Mão de obra consumida"
              icon={Zap} tone="warning" />
            <StatCard testId="prod-kpi-tecnicos" label="Técnicos recomendados"
              value={fmtInt(tecnicosRecomendados)}
              hint={`Base: ${HORAS_TECNICO_MES}h úteis/técnico`}
              icon={Users} tone={tecnicosRecomendados > 2 ? "warning" : "success"} />
            <StatCard testId="prod-kpi-plano-pesado" label="Instalação mais longa"
              value={maisPesado ? `${maisPesado.horasPorInstalacao}h` : "—"}
              hint={maisPesado ? maisPesado.plano : "—"}
              icon={Timer} tone="danger" />
          </div>

          {/* Insight comparativo */}
          {maisPesado && maisLeve && maisPesado.plano !== maisLeve.plano && (
            <div className="mb-5 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 flex items-start gap-3">
              <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <strong className="text-amber-500">{maisPesado.plano}</strong> leva{" "}
                <span className="font-mono">{maisPesado.horasPorInstalacao}h</span> de instalação,{" "}
                {maisLeve.horasPorInstalacao > 0
                  ? <>enquanto <strong>{maisLeve.plano}</strong> leva apenas <span className="font-mono">{maisLeve.horasPorInstalacao}h</span> — ou seja, o {maisPesado.plano} consome <strong>{(maisPesado.horasPorInstalacao / maisLeve.horasPorInstalacao).toFixed(1)}×</strong> mais mão de obra.</>
                  : <>e o {maisLeve.plano} ainda não tem horas cadastradas.</>}
                {" "}Ideal reservar técnicos extras em dias de venda de <strong>{maisPesado.plano}</strong>.
              </div>
            </div>
          )}

          {/* Gráfico horas totais por plano no período */}
          <div className="h-64 mb-5" data-testid="produtividade-chart">
            {chartData.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                Sem vendas no período — cadastre clientes ou selecione outro mês.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 24, right: 12, left: -8, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="plano" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${v}h`} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    formatter={(val, name, ctx) => {
                      const r = ctx?.payload;
                      return [`${val}h`, `Total · ${r?.vendas ?? 0} venda(s) × ${r?.horasUnit ?? 0}h`];
                    }}
                  />
                  <Bar dataKey="horas" radius={[8, 8, 0, 0]}>
                    {chartData.map((row, idx) => (
                      <Cell key={row.plano || row.name || `plano-${idx}`} fill={PLAN_COLORS[idx % PLAN_COLORS.length]} />
                    ))}
                    <LabelList dataKey="horas" position="top"
                      formatter={(v) => (v > 0 ? `${v}h` : "")}
                      style={{ fill: "hsl(var(--foreground))", fontSize: 11, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Tabela detalhada */}
          <div className="space-y-2" data-testid="produtividade-table">
            <div className="grid grid-cols-[1fr_80px_100px_100px_80px_100px_1fr] gap-2 text-[10px] uppercase tracking-wider text-muted-foreground px-2">
              <div>Plano</div>
              <div className="text-right">h/instal.</div>
              <div className="text-right">R$/hora</div>
              <div className="text-right">Custo MO/inst.</div>
              <div className="text-right">Vendas</div>
              <div className="text-right">Horas totais</div>
              <div>MO no custo</div>
            </div>
            {rows.map((r, idx) => {
              const moTone =
                r.pctMOnoCusto >= 40 ? "text-red-500" :
                r.pctMOnoCusto >= 25 ? "text-amber-500" : "text-emerald-500";
              return (
                <div key={r.plano}
                  className="grid grid-cols-[1fr_80px_100px_100px_80px_100px_1fr] gap-2 items-center text-sm rounded-md border border-border px-2 py-2"
                  data-testid={`prod-row-${r.plano}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PLAN_COLORS[idx % PLAN_COLORS.length] }} />
                    <span className="truncate font-medium">{r.plano}</span>
                  </div>
                  <div className="text-right tabular-nums font-mono">
                    {r.horasPorInstalacao > 0 ? `${r.horasPorInstalacao}h` : "—"}
                  </div>
                  <div className="text-right tabular-nums font-mono text-muted-foreground">
                    {r.custoHoraUnit > 0 ? fmtBRL(r.custoHoraUnit) : "—"}
                  </div>
                  <div className="text-right tabular-nums font-mono">
                    {r.custoMOInstalacao > 0 ? fmtBRL(r.custoMOInstalacao) : "—"}
                  </div>
                  <div className="text-right tabular-nums">{fmtInt(r.vendas)}</div>
                  <div className="text-right tabular-nums font-mono font-semibold">
                    {r.horasTotais > 0 ? `${r.horasTotais.toFixed(1)}h` : "—"}
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <Progress value={Math.min(r.pctMOnoCusto, 100)} className="h-1.5 flex-1" />
                    <span className={`text-xs font-semibold tabular-nums w-14 text-right ${moTone}`}>
                      {r.pctMOnoCusto > 0 ? fmtPct(r.pctMOnoCusto) : "—"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-[11px] text-muted-foreground mt-3">
            <strong>MO no custo</strong> = participação da mão de obra no custo total do plano.
            <span className="text-emerald-500"> Verde</span> ≤ 25%, <span className="text-amber-500">amarelo</span> ≤ 40%,{" "}
            <span className="text-red-500">vermelho</span> {'>'} 40%.
            Recomendação de técnicos considera {HORAS_TECNICO_MES}h úteis/mês por técnico.
          </p>
        </>
      )}
    </div>
  );
}
